import React from 'react'
import styles from './TRoutePhotoUploader.scss'
import {List, Map} from 'immutable'
import {ImageUpload} from 'common/styleguide/ImageUpload'
import plusIcon from 'assets/images/goals/plusIcon.svg'
import {ProgressBar} from 'common/ProgressBar'
import {photoActions} from 'data/resources/photos'
import {tripActions} from 'data/resources/trips'
import {routeActions} from 'data/resources/routes'
import {Icon} from 'common/Icon'
import {connect} from 'react-redux'
import cx from 'classnames'

const trouteActions = objectType => objectType === 'trip' ? tripActions : routeActions

const UPLOAD_CONCURRENCY = 3

// uploader/manager for TRoute photos
class _TRoutePhotoUploader extends React.PureComponent {
  state = {
    files: List(), // List<File> of all files that we know about, in order of drop
    pendingUploads: List(), // List<File> list of uploads that have not been started
    activeUploads: Map(), // Map<File->Promise> map of uploads actively in progress
    completedUploads: Map(), // Map<File->photoId> map of uploads that have completed successfully
    lastUploadSpeed: 55 // bytes/millisecond of the last photo upload. Default value is a random sample from a typical image upload over coffee shop wifi.
  }

  onDrop = (toQueue) => {
    if (!toQueue[0]) { return }
    const {files, pendingUploads} = this.state
    this.setState({
      files: files.concat(toQueue),
      pendingUploads: pendingUploads.concat(toQueue)
    })
  }

  // when any of the props change, queue uploads as needed.
  componentDidUpdate = () => {
    const {pendingUploads, activeUploads} = this.state
    if (activeUploads.size < UPLOAD_CONCURRENCY && pendingUploads.size > 0) {
      const toQueue = pendingUploads.take(UPLOAD_CONCURRENCY - activeUploads.size)
      const remaining = pendingUploads.slice(toQueue.size)
      const toUpload = toQueue.toArray().map(file => {
        const promise = this.uploadPhoto(file)
        return [file, promise]
      })
      this.setState({
        pendingUploads: remaining,
        activeUploads: activeUploads.concat(toUpload)
      })
    }
  }

  uploadPhoto = (file) => {
    const {objectType, troute, dispatch} = this.props

    const body = new window.FormData()
    body.append('file', file)
    body.append('parent_type', objectType)
    body.append('parent_id', troute.id)

    const uploadStarted = Date.now()
    const upload = dispatch(photoActions.create(body))

    upload.then(r =>
      dispatch(trouteActions(objectType).photoAdded(troute.id, r.action.payload.photo.id))
    )

    upload.then(r => {
      const timeElapsed = Date.now() - uploadStarted
      const lastUploadSpeed = file.size / timeElapsed
      console.log('lastUploadSpeed', lastUploadSpeed)
      this.setState({
        lastUploadSpeed,
        activeUploads: this.state.activeUploads.delete(file),
        completedUploads: this.state.completedUploads.set(file, r.action.payload.photo.id)
      })
    })

    return upload
  }

  onRequestRemove = (file) => {
    const {troute, objectType, dispatch} = this.props
    const {pendingUploads, completedUploads, files} = this.state
    // remove from backend
    const photoId = completedUploads.get(file)
    if (photoId) {
      dispatch(photoActions.delete(photoId))
        .then(r => dispatch(
          trouteActions(objectType).photoRemoved(troute.id, photoId)
        ))
    }
    this.setState({
      pendingUploads: pendingUploads.delete(pendingUploads.indexOf(file)),
      completedUploads: completedUploads.delete(file),
      files: files.delete(files.indexOf(file))
    })
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  render () {
    const {objectType} = this.props
    const {files, pendingUploads, activeUploads, completedUploads, lastUploadSpeed} = this.state
    const noImages = files.size === 0
    return (
      <ImageUpload
        ref={r => { this._dropzone = r }}
        multiple
        disableClick
        onDrop={this.onDrop}
        className={styles.TRoutePhotoUploader}
        hintTop={`Drop photos for your ${objectType} here`}
        hintBottom={<span>You can also use the <a onClick={this.onTriggerFileBrowser}>File Browser</a></span>}>
        {noImages && <img className={styles.plusIcon} height='80' width='80' src={plusIcon} />}
        <div className={styles.grid}>
          {files.map(f =>
            <ImageThumb
              file={f}
              onRequestRemove={this.onRequestRemove}
              pending={pendingUploads.includes(f)}
              inProgress={activeUploads.has(f)}
              completed={completedUploads.has(f)}
              lastUploadSpeed={lastUploadSpeed} />)}
        </div>
      </ImageUpload>
    )
  }
}

export const TRoutePhotoUploader = connect(
  null,
  dispatch => ({dispatch})
)(_TRoutePhotoUploader)

class ImageThumb extends React.PureComponent {
  render () {
    const {file, pending, inProgress, completed, lastUploadSpeed, onRequestRemove} = this.props
    return (
      <div className={cx(styles.ImageThumb,
        pending && styles.pending,
        inProgress && styles.inProgress,
        completed && styles.completed)}>
        <img
          className={styles.thumb}
          src={file.preview}
          height={81}
          width={81} />
        {(pending || inProgress) && <DummyProgressBar
          key={!!inProgress} // force remount when upload starts
          active={inProgress}
          duration={file.size / lastUploadSpeed} />}
        <div className={styles.delete} onClick={() => onRequestRemove(file)}>
          <Icon i='md-close' size={16} className={styles.icon} />
        </div>
      </div>
    )
  }
}

class DummyProgressBar extends React.PureComponent {
  state = {
    progress: 0.1
  }

  componentDidMount () {
    const {active, duration} = this.props
    if (!active) { return }
    const jitterFactor = 0.05 * duration

    ;[0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9]
      .sort(() => Math.round(Math.random() - 0.5)).slice(2) // randomly kill two stops
      .concat([1]) // add the terminal (1) stop
      .forEach(progress => {
        const jitter = (Math.random() * 2 - 1) * jitterFactor
        const timeout = progress * duration + jitter
        setTimeout(() => this.setState({progress}), timeout)
      })
  }

  render () {
    const {progress} = this.state

    return (
      <ProgressBar
        height='3px'
        width='81px'
        styleInner={{transition: 'width 0.2s ease'}}
        progress={progress} />
    )
  }
}
